import React, { useState, useEffect } from "react";
import {
  Radar,
} from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Default export React component
export default function BlueHandsAssessment() {
  const stakeholderGroups = {
    worker: { name: "Worker (Self)", color: "#14b8a6" },
    employer: { name: "Principal Employer", color: "#0d9488" },
    contractor: { name: "Contractor / Agency", color: "#00bfa6" },
  };

  const baseQuestions = [
    // Safety & Wellbeing
    { id: "q1", workerLabel: "I always wear and maintain personal protective equipment (PPE) like helmets, gloves, masks, and safety shoes.", employerLabel: "We provide comprehensive PPE, regular safety training, and ensure its proper use across all sites.", contractorLabel: "We supply verified PPE to all deployed workers and conduct daily safety checks for compliance.", pillar: "Safety & Wellbeing" },
    { id: "q2", workerLabel: "I report hazards, near-misses, or health issues immediately and follow emergency protocols.", employerLabel: "We conduct regular safety audits, risk assessments, and maintain a zero-tolerance policy for unsafe conditions.", contractorLabel: "We train workers on hazard reporting and ensure quick resolution of safety incidents on-site.", pillar: "Safety & Wellbeing" },
    { id: "q3", workerLabel: "I participate in health check-ups, hygiene practices, and wellness initiatives provided at work.", employerLabel: "We organize periodic health camps, promote mental health support, and comply with occupational health standards.", contractorLabel: "We facilitate access to health insurance (ESI) and wellness programs for our workforce.", pillar: "Safety & Wellbeing" },

    // Skills & Growth
    { id: "q4", workerLabel: "I actively seek and complete skill training or certification programs relevant to my role.", employerLabel: "We invest in structured upskilling programs, apprenticeships, and career progression pathways for workers.", contractorLabel: "We prioritize hiring certified/skilled workers and provide ongoing training to enhance their capabilities.", pillar: "Skills & Growth" },
    { id: "q5", workerLabel: "I use digital tools or apps for learning new techniques or tracking my professional development.", employerLabel: "We integrate digital learning platforms and track skill gaps through performance reviews.", contractorLabel: "We use digital systems to match workers' skills with job requirements and monitor training progress.", pillar: "Skills & Growth" },
    { id: "q6", workerLabel: "I apply learned skills to improve my daily work efficiency and suggest process improvements.", employerLabel: "We encourage knowledge sharing and reward innovations from the workforce.", contractorLabel: "We foster a culture of continuous learning and skill application in our deployment strategies.", pillar: "Skills & Growth" },

    // Work Ethics
    { id: "q7", workerLabel: "I maintain punctuality, attendance, and a positive, respectful attitude towards colleagues and supervisors.", employerLabel: "We promote a culture of integrity, punctuality, and mutual respect through clear policies and recognition programs.", contractorLabel: "We enforce ethical standards, attendance tracking, and disciplinary processes fairly across our teams.", pillar: "Work Ethics" },
    { id: "q8", workerLabel: "I handle tools, materials, and resources responsibly to minimize waste and damage.", employerLabel: "We implement accountability measures for resource use and ethical conduct in all operations.", contractorLabel: "We monitor resource stewardship and ethical practices to build trust with clients and workers.", pillar: "Work Ethics" },
    { id: "q9", workerLabel: "I collaborate effectively in teams and contribute to a harmonious work environment.", employerLabel: "We build team-building initiatives and conflict resolution mechanisms for a cohesive workforce.", contractorLabel: "We promote teamwork and ethical collaboration in multi-contractor project environments.", pillar: "Work Ethics" },

    // Compliance & Wages
    { id: "q10", workerLabel: "I am aware of my rights regarding wages, working hours, overtime, and statutory benefits.", employerLabel: "We ensure 100% compliance with labor laws, timely wage payments, and transparent benefit structures.", contractorLabel: "We maintain accurate payroll records, deduct only statutory amounts, and ensure PF/ESI contributions.", pillar: "Compliance & Wages" },
    { id: "q11", workerLabel: "I receive fair compensation, including bonuses or incentives, without unauthorized deductions.", employerLabel: "We conduct regular wage audits and provide grievance redressal for payment disputes.", contractorLabel: "We adhere to minimum wage norms and resolve payment issues within 48 hours.", pillar: "Compliance & Wages" },
    { id: "q12", workerLabel: "I understand and follow documentation requirements like ID proofs, contracts, and leave policies.", employerLabel: "We digitize compliance records and ensure all workers have access to their employment documents.", contractorLabel: "We handle all statutory filings and maintain digital worker profiles for audit readiness.", pillar: "Compliance & Wages" },

    // Communication & Feedback
    { id: "q13", workerLabel: "I communicate clearly with supervisors about tasks, challenges, and feedback using available channels.", employerLabel: "We have open, multi-channel communication systems (apps, meetings) for worker input and updates.", contractorLabel: "We facilitate seamless communication between workers, employers, and site teams.", pillar: "Communication & Feedback" },
    { id: "q14", workerLabel: "I receive constructive feedback on my performance and opportunities for improvement.", employerLabel: "We implement regular feedback loops, surveys, and action plans based on worker suggestions.", contractorLabel: "We collect and act on feedback from workers to refine deployment and support processes.", pillar: "Communication & Feedback" },
    { id: "q15", workerLabel: "I feel valued and heard in discussions about workplace changes or decisions affecting me.", employerLabel: "We involve workers in decision-making through committees or digital polls for inclusivity.", contractorLabel: "We act as a bridge for worker voices, ensuring their concerns reach principal employers effectively.", pillar: "Communication & Feedback" },
  ];

  const initialAnswers = {};
  Object.keys(stakeholderGroups).forEach((stakeholder) => {
    initialAnswers[stakeholder] = {};
    baseQuestions.forEach((q) => (initialAnswers[stakeholder][q.id] = 1));
  });

  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem("bluehands_assessment_answers");
      return saved ? JSON.parse(saved) : initialAnswers;
    } catch (e) {
      return initialAnswers;
    }
  });

  const [currentStakeholder, setCurrentStakeholder] = useState("worker");

  useEffect(() => {
    localStorage.setItem("bluehands_assessment_answers", JSON.stringify(answers));
  }, [answers]);

  const handleChange = (stakeholder, id, value) => {
    setAnswers((s) => ({ ...s, [stakeholder]: { ...s[stakeholder], [id]: Number(value) } }));
  };

  const totalScore = (stakeholder) => {
    return Object.values(answers[stakeholder] || {}).reduce((a, b) => a + b, 0);
  };

  const maxScore = () => {
    return baseQuestions.length * 5;
  };

  const levelFromScore = (score) => {
    const scaled = Math.round((score / maxScore()) * 75);
    if (scaled <= 25) return { level: 1, name: "Level 1 – Vulnerable / Manual" };
    if (scaled <= 40) return { level: 2, name: "Level 2 – Emerging / Basic" };
    if (scaled <= 55) return { level: 3, name: "Level 3 – Structured & Disciplined" };
    if (scaled <= 65) return { level: 4, name: "Level 4 – Responsible & Compliant" };
    return { level: 5, name: "Level 5 – Progressive & Sustainable" };
  };

  const getRecommendations = (level, stakeholder) => {
    const maps = {
      worker: {
        1: ["Learn basic safety rules and PPE usage from supervisors.", "Track your attendance and seek simple skill tips daily.", "Ask for clear instructions on tasks to build confidence."],
        2: ["Attend any available safety or hygiene workshops.", "Practice punctuality and report issues openly.", "Explore free online resources for basic job skills."],
        3: ["Participate actively in training sessions.", "Document your achievements and seek feedback regularly.", "Contribute ideas for safer work practices."],
        4: ["Pursue certifications in your trade.", "Build a personal development plan with goals.", "Advocate for better communication channels."],
        5: ["Mentor peers on best practices.", "Lead small improvement initiatives.", "Engage in advanced digital learning for career growth."],
      },
      employer: {
        1: ["Start with basic PPE provision and safety signage.", "Implement simple attendance tracking.", "Conduct introductory welfare meetings."],
        2: ["Roll out mandatory safety inductions for all.", "Set up fair wage policies with transparency.", "Introduce basic grievance helplines."],
        3: ["Audit compliance quarterly and train supervisors.", "Use digital tools for payroll and feedback.", "Foster inclusive team-building activities."],
        4: ["Integrate health camps and skill programs annually.", "Monitor contractor performance on welfare metrics.", "Create dashboards for workforce insights."],
        5: ["Embed ESG in operations with worker involvement.", "Partner for advanced training academies.", "Use AI for predictive safety and engagement."],
      },
      contractor: {
        1: ["Ensure basic worker documentation and ID checks.", "Verify PPE before deployment.", "Set clear payment timelines."],
        2: ["Train on statutory compliance basics.", "Track attendance digitally.", "Resolve disputes informally but promptly."],
        3: ["Conduct toolbox talks on safety weekly.", "Maintain PF/ESI filings accurately.", "Build liaison protocols with employers."],
        4: ["Certify workers on skills and audit sites.", "Implement feedback loops with workers.", "Diversify skilled talent pools."],
        5: ["Use data analytics for deployment optimization.", "Lead cluster-wide safety standards.", "Foster long-term worker-employer partnerships."],
      },
    };
    return maps[stakeholder][level] || [];
  };

  const pillarScores = (stakeholder) => {
    const pillarMap = {};
    baseQuestions.forEach((q) => {
      pillarMap[q.pillar] = (pillarMap[q.pillar] || 0) + answers[stakeholder][q.id];
    });

    const pillarCounts = {};
    baseQuestions.forEach((q) => (pillarCounts[q.pillar] = (pillarCounts[q.pillar] || 0) + 1));

    const data = Object.keys(pillarMap).map((p) => ({
      subject: p,
      A: +(pillarMap[p] / pillarCounts[p]).toFixed(2),
      fullMark: 5,
    }));
    return data;
  };

  const resetAll = () => {
    const ia = {};
    Object.keys(stakeholderGroups).forEach((stakeholder) => {
      ia[stakeholder] = {};
      baseQuestions.forEach((q) => (ia[stakeholder][q.id] = 1));
    });
    setAnswers(ia);
    localStorage.removeItem("bluehands_assessment_answers");
  };

  const exportJSON = () => {
    const payload = {
      answers,
      scores: Object.fromEntries(Object.keys(stakeholderGroups).map(s => [s, totalScore(s)])),
      levels: Object.fromEntries(Object.keys(stakeholderGroups).map(s => [s, levelFromScore(totalScore(s))])),
      date: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bluehands_assessment.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const currentScore = totalScore(currentStakeholder);
  const currentLevelObj = levelFromScore(currentScore);
  const currentRecs = getRecommendations(currentLevelObj.level, currentStakeholder);
  const currentChartData = pillarScores(currentStakeholder);

  // Prepare data for Chart.js Radar
  const labels = currentChartData.map((d) => d.subject);
  const dataPoints = currentChartData.map((d) => d.A);

  const radarData = {
    labels,
    datasets: [
      {
        label: "Average Score",
        data: dataPoints,
        backgroundColor: `rgba(${currentStakeholder === 'worker' ? '20,184,166' : currentStakeholder === 'employer' ? '13,148,136' : '0,191,166'}, 0.2)`,
        borderColor: stakeholderGroups[currentStakeholder].color,
        borderWidth: 2,
        pointBackgroundColor: stakeholderGroups[currentStakeholder].color,
        pointBorderColor: stakeholderGroups[currentStakeholder].color,
        pointHoverBackgroundColor: stakeholderGroups[currentStakeholder].color,
        pointHoverBorderColor: stakeholderGroups[currentStakeholder].color,
      },
    ],
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
          backdropColor: "transparent",
          color: "#94a3b8",
        },
        grid: {
          color: "rgba(148, 163, 184, 0.3)",
        },
        pointLabels: {
          font: {
            size: 12,
            family: "inherit",
            color: "#e2e8f0",
          },
        },
        angleLines: {
          color: "rgba(148, 163, 184, 0.3)",
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          color: "#e2e8f0",
        },
      },
      tooltip: {
        backgroundColor: "#1e293b",
        titleColor: "#e2e8f0",
        bodyColor: "#e2e8f0",
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.r}`;
          },
        },
      },
    },
    interaction: {
      intersect: false,
    },
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto bg-slate-900 min-h-screen">
      <h1 className="text-lg lg:text-xl font-bold mb-2 text-emerald-400 text-center">Blue Hands Workforce Maturity — Self Assessment</h1>
      <p className="text-sm text-gray-400 mb-6 text-center">Assess maturity across Worker (Self), Principal Employer, and Contractor perspectives on a 1–5 scale. View pillar strengths and tailored recommendations for blue-collar ecosystems.</p>

      {/* Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {Object.entries(stakeholderGroups).map(([key, { name, color }]) => {
          const s = totalScore(key);
          const l = levelFromScore(s);
          return (
            <div key={key} className="bg-slate-800 border border-emerald-500 shadow rounded-lg p-4 cursor-pointer hover:bg-slate-700 transition-colors" onClick={() => setCurrentStakeholder(key)}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide">{name}</div>
                  <div className="text-xl font-extrabold text-white">{s} / {maxScore()}</div>
                  <div className="text-xs mt-1 text-emerald-400">{l.name}</div>
                </div>
                <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: color }}></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-slate-800 border border-blue-500 shadow rounded-lg p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 lg:gap-0">
          <div className="flex flex-col">
            <div className="text-sm text-gray-400">Current: {stakeholderGroups[currentStakeholder].name}</div>
            <div className="text-xl lg:text-3xl font-extrabold text-white">{currentScore} / {maxScore()}</div>
            <div className="text-sm mt-1 text-emerald-400">{currentLevelObj.name}</div>
          </div>

          <div className="flex flex-row items-center gap-2">
            <button onClick={resetAll} className="px-3 py-1 rounded border border-gray-600 text-gray-300 hover:bg-gray-700 w-full lg:w-auto">Reset</button>
            <button onClick={exportJSON} className="px-3 py-1 w-full lg:w-48 rounded bg-emerald-500 text-black hover:bg-emerald-600">Export JSON</button>
          </div>
        </div>
      </div>

      {/* Questions Form for Current Stakeholder */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {baseQuestions.map((q) => {
          const label = currentStakeholder === 'worker' ? q.workerLabel : currentStakeholder === 'employer' ? q.employerLabel : q.contractorLabel;
          return (
            <div key={q.id} className="bg-slate-700 p-4 rounded shadow-sm">
              <label className="block text-sm font-medium mb-2 text-gray-300">{label}</label>
              <div className="flex items-center gap-2 flex-wrap">
                {[1, 2, 3, 4, 5].map((n) => (
                  <label key={n} className={`inline-flex items-center cursor-pointer px-2 py-1 rounded ${answers[currentStakeholder][q.id] === n ? "bg-emerald-500 text-black font-semibold" : "text-gray-400 hover:bg-slate-600"}`}>
                    <input 
                      type="radio" 
                      name={q.id} 
                      value={n} 
                      checked={answers[currentStakeholder][q.id] === n} 
                      onChange={(e) => handleChange(currentStakeholder, q.id, e.target.value)} 
                      className="mr-1 hidden" 
                    />
                    {n}
                  </label>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-2">Pillar: {q.pillar}</div>
            </div>
          );
        })}
      </div>

      {/* Radar and Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="col-span-1 md:col-span-2 bg-slate-700 p-4 rounded shadow">
          <h3 className="font-semibold mb-2 text-emerald-400">Pillar Radar (Average 1–5)</h3>
          <div style={{ position: "relative", height: "300px", width: "100%" }}>
            <Radar data={radarData} options={radarOptions} />
          </div>
        </div>

        <div className="bg-slate-700 p-4 rounded shadow">
          <h3 className="font-semibold mb-2 text-emerald-400">Recommendations</h3>
          <div className="text-sm text-gray-300 space-y-2">
            <div className="font-medium">Stage: {currentLevelObj.name}</div>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              {currentRecs.map((r, idx) => (
                <li key={idx}>{r}</li>
              ))}
            </ul>
            <div className="mt-4">
              <button onClick={() => window.print()} className="px-3 py-1 rounded border border-gray-600 text-gray-300 hover:bg-gray-600 w-full">Print</button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 text-xs text-gray-500 text-center">Tip: Switch between stakeholders to assess the full ecosystem. Export JSON for facilitators, unions, or compliance audits. Focus on pillars to prioritize training and improvements.</div>
    </div>
  );
}